import React from "react";
import styled from "styled-components";

export interface ProductCardProps {
  badge?: string;
  title: string;
  subtitle_insurancetype: string;
  subtitle_description: string;
  age_range?: string;
  insurance_logo: string;
  benefit_items: string[];
  prices: number;
  category?: string;
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 335px;
  height: 495px;
  border-radius: 8px;
  position: relative;
  background-color: transparent;
`;

const ChipsBadge = styled.div<{ badgeText?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${(props) =>
    props.badgeText && props.badgeText.includes("Best seller")
      ? "#DA3B45"
      : "#1561E8"};
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 8px 8px 8px 0;
  z-index: 10;
  line-height: 1.2;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 479px;
  border: 1px solid #d9dbde;
  border-radius: 8px;
  margin-top: 8px; /* mt-2 */
  background-color: #ffffff;
  overflow: hidden;
  box-sizing: border-box;
`;

const TopDetailsSec = styled.div`
  display: flex;
  padding: 16px;
  gap: 8px;
  background: linear-gradient(90deg, #f5f6f7 0%, #ffffff 100%);
  border-bottom: 1px solid #d9dbde;
  justify-content: space-between;
  align-items: flex-start;
  height: 143px;
  box-sizing: border-box;
`;

const TopDetailsLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex-grow: 1;
  padding-top: 6px;
`;

const SubtitleInsuranceType = styled.span`
  color: #8a43ff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
`;

const Title = styled.h3`
  color: #202123;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;

  height: 56px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
`;

const SubtitleDescription = styled.p`
  color: #202123;
  font-size: 12px;
  margin: 0;
  line-height: 1.5;
`;

const AgeRange = styled.span`
  color: #435dff;
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
`;

const InsuranceLogo = styled.img`
  width: 40px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
`;

const MiddleBenefitsSec = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px; /* padding-4 */
  gap: 16px; /* gap-4 */
  background-color: #ffffff;
  flex-grow: 1;
  justify-content: space-between;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const IconPlaceholder = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
  flex: none;
  order: 0;
  flex-grow: 0;
  margin-top: -2px; /* Align with first line of text */
`;

const VectorStroke = styled.svg`
  position: absolute;
  left: 3.25px;
  top: 5.25px;
  width: 17.5px;
  height: 12.5px;
`;

const CheckIcon = () => (
  <VectorStroke
    viewBox="0 0 19 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.25 11L9.5 17.75L20.75 5.25"
      stroke="#29801A"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </VectorStroke>
);

const BenefitText = styled.span`
  color: #202123;
  font-size: 14px;
  line-height: 1.4;
`;

const BottomButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 32px;
  padding: 0 16px 0 16px;
  border: 1px solid #d9dbde;
  border-radius: 9999px; /* rounded-full */
  background-color: transparent;
  color: #435dff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  outline: none;

  &:hover {
    background-color: rgba(67, 93, 255, 0.05);
    border-color: #304eff;
    color: #304eff;
  }
`;

const BottomPricingSec = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #ffffff;
  border-top: 1px solid #d9dbde;
`;

const PriceLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const PriceLabel = styled.span`
  color: #5f6468;
  font-size: 15px;
  line-height: 1.3;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const PriceValue = styled.span`
  color: #435dff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
`;

const PriceUnit = styled.span`
  color: #000000;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`;

const CTAButton = styled.button`
  background-color: #435dff;
  color: #ffffff;
  font-weight: 600;
  font-size: 16px;
  padding: 12px 24px; /* px-6 py-3 */
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  white-space: nowrap;
  outline: none;

  &:hover {
    background-color: #304eff;
  }
`;

const val: any = () => null;
val.isRequired = () => null;

export const ProductCard = ({
  badge,
  title,
  subtitle_insurancetype,
  subtitle_description,
  age_range,
  insurance_logo,
  benefit_items,
  prices,
}: ProductCardProps) => {
  return (
    <Card>
      {badge && <ChipsBadge badgeText={badge}>{badge}</ChipsBadge>}
      <ContentContainer>
        <TopDetailsSec>
          <TopDetailsLeft>
            <SubtitleInsuranceType>
              {subtitle_insurancetype}
            </SubtitleInsuranceType>
            <Title>{title}</Title>
            <SubtitleDescription>{subtitle_description}</SubtitleDescription>
            {age_range && <AgeRange>{age_range}</AgeRange>}
          </TopDetailsLeft>
          <InsuranceLogo src={insurance_logo} alt="Insurance Logo" />
        </TopDetailsSec>

        <MiddleBenefitsSec>
          <BenefitsList>
            {benefit_items.map((benefit) => (
              <BenefitItem key={benefit}>
                <IconPlaceholder>
                  <CheckIcon />
                </IconPlaceholder>
                <BenefitText>{benefit}</BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
          <BottomButton> More info</BottomButton>
        </MiddleBenefitsSec>

        <BottomPricingSec>
          <PriceLabelContainer>
            <PriceLabel>Starting premium</PriceLabel>
            <PriceWrapper>
              <PriceValue>{prices.toLocaleString()}</PriceValue>
              <PriceUnit>Baht/year</PriceUnit>
            </PriceWrapper>
          </PriceLabelContainer>
          <CTAButton>Buy now</CTAButton>
        </BottomPricingSec>
      </ContentContainer>
    </Card>
  );
};

ProductCard.propTypes = {
  badge: val,
  title: val.isRequired,
  subtitle_insurancetype: val.isRequired,
  subtitle_description: val.isRequired,
  age_range: val,
  insurance_logo: val.isRequired,
  benefit_items: val.isRequired,
  prices: val.isRequired,
};

ProductCard.defaultProps = {
  badge: undefined,
  age_range: undefined,
};

export default ProductCard;
